import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  Link as MuiLink,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import {
  AutogeneratedDraftsContext,
  TabPage,
} from "contexts/AutogenerateDrafts";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { OutreachDraft, UpgradeDialogSource } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";
import { HEART_COLOR } from "styles/oneOffColor";

import { AI_DRAFT_CREDITS_TOOLTIP } from "constants/influencer";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useDrafts } from "utils/useDrafts";

import PickCollectionDropdown from "./PickCollectionDropdown";
import SelectedBrand from "./SelectedBrand";
import styles from "./styles";

const REF_OFFSET = 5;

interface Props {
  endRef: any;
}

const DraftsBrandList = ({ endRef }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    setProfileDialogOpen,
    setCreateTemplateOnboardingDialogOpen,
    incompleteProfile,
    noTemplates,
  } = useContext(OnboardingDialogsContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { setUpgradeDialogSource, subscription } =
    useContext(SubscriptionContext);

  const incompleteOnboarding = incompleteProfile || noTemplates;

  const { tab } = useParams();
  const {
    isPolling,
    fetchDrafts,
    frozenIds,
    fetchLoading,
    pollForNewDrafts,
    requestMoreDrafts,
    isGeneratingMore,
    setDraftsMap,
    setFetchLoading,
  } = useContext(AutogeneratedDraftsContext);
  const { getDrafts } = useDrafts();
  const drafts = getDrafts(tab);

  const [prevDrafts, setPrevDrafts] = useState<OutreachDraft[]>([]);

  const handleChange = (e: CustomEvent, value: TabPage) => {
    fetchDrafts(value);
    navigate(`/${routes.recommendations}/${value}${location.search}`);
  };

  const handleRefreshBrands = async () => {
    if (!currentOrg) return;
    if (!(subscription && subscription?.remainingChatMessages > 0)) {
      setUpgradeDialogSource(
        UpgradeDialogSource.hits_personalized_ai_messages_limit_from_refresh,
      );
      return;
    }

    setFetchLoading(true);

    setDraftsMap((prev) => {
      const copy = makeDeepCopy(prev);
      setPrevDrafts(copy[TabPage.bento_recommendations]["drafts"]);
      copy[TabPage.bento_recommendations]["drafts"] = [];
      return copy;
    });

    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/outreach-drafts/refresh`,
        "POST",
      );
      pollForNewDrafts(TabPage.bento_recommendations, undefined, [], true);
    } catch (error) {
      if (error?.message.includes("upgrade your account")) {
        setUpgradeDialogSource(
          UpgradeDialogSource.hits_personalized_ai_messages_limit_from_refresh,
        );
        return;
      }
      setErrorAlert(error);
      setDraftsMap((prev) => {
        const copy = makeDeepCopy(prev);
        copy[TabPage.bento_recommendations]["drafts"] = prevDrafts;
        return copy;
      });
    }
  };

  const handleRequestMore = async () => {
    if (!currentOrg || isGeneratingMore) return;
    if (!(subscription && subscription?.remainingChatMessages > 0)) {
      setUpgradeDialogSource(
        UpgradeDialogSource.hits_personalized_ai_messages_limit_from_refresh,
      );
      return;
    }

    try {
      await requestMoreDrafts(TabPage.bento_recommendations);
    } catch (error) {
      if (error?.message.includes("upgrade your account")) {
        setUpgradeDialogSource(
          UpgradeDialogSource.hits_personalized_ai_messages_limit_from_refresh,
        );
        return;
      }
      setErrorAlert(error);
    }
  };

  return (
    <>
      <Grid item xs sx={isMobileScreen ? {} : styles.brandListContainer}>
        <Box>
          <Tabs value={tab} onChange={handleChange}>
            <Tab
              sx={styles.tab}
              value={TabPage.favorite_drafts}
              label={
                <Box component="span">
                  Saved{" "}
                  <Box
                    component="i"
                    sx={{ color: HEART_COLOR, ml: 0.5 }}
                    className="fa-solid fa-bookmark"
                  />
                </Box>
              }
            />
            <Tab
              sx={styles.tab}
              value={TabPage.bento_recommendations}
              label="Recommendations"
            />
          </Tabs>

          {tab === TabPage.favorite_drafts ? (
            <PickCollectionDropdown />
          ) : (
            <Typography sx={styles.subtitle} variant="body2">
              You have{" "}
              <strong>
                {subscription?.remainingChatMessages} AI-generated drafts
              </strong>{" "}
              <Tooltip
                slotProps={{
                  tooltip: {
                    sx: {
                      color: "black",
                      backgroundColor: "white",
                      p: 1,
                      boxShadow: 1,
                    },
                  },
                }}
                title={AI_DRAFT_CREDITS_TOOLTIP}
                enterTouchDelay={0}
              >
                <Box
                  sx={{ mx: 0.5, color: "info.main" }}
                  component="i"
                  className="fa-solid fa-circle-info"
                />
              </Tooltip>
              left.{" "}
              {!subscription?.isSubscribed && (
                <>
                  To get more,{" "}
                  <MuiLink
                    sx={{ cursor: "pointer", fontWeight: 600 }}
                    onClick={() =>
                      setUpgradeDialogSource(
                        UpgradeDialogSource.hits_personalized_ai_messages_limit,
                      )
                    }
                  >
                    upgrade
                  </MuiLink>{" "}
                  to one of our paid plans
                </>
              )}
            </Typography>
          )}
        </Box>

        {drafts.map((draft: OutreachDraft, index: number) => (
          <SelectedBrand
            key={draft.bentoBrand.id}
            ref={drafts.length - REF_OFFSET === index ? endRef : null}
            bentoBrand={draft.bentoBrand}
          />
        ))}

        {(isPolling[tab as TabPage] || fetchLoading) &&
          (frozenIds?.length > 0 || drafts?.length === 0) && (
            <Grid sx={{ px: 2 }}>
              {Array.from({ length: frozenIds?.length || 10 }, (x, i) => (
                <Skeleton
                  variant="rounded"
                  width={"100%"}
                  height={80}
                  sx={{ my: 1 }}
                  key={i}
                />
              ))}
            </Grid>
          )}

        {tab === TabPage.bento_recommendations &&
          !incompleteOnboarding &&
          !isPolling[tab as TabPage] &&
          !fetchLoading &&
          drafts?.length > 0 && (
            <Box
              sx={styles.moreRow}
              onClick={isGeneratingMore ? undefined : handleRequestMore}
              role="button"
              aria-disabled={isGeneratingMore}
            >
              {isGeneratingMore ? (
                <>
                  <CircularProgress size={14} color="inherit" />
                  <Typography sx={styles.moreRowLabel}>
                    Finding more recommendations…
                  </Typography>
                </>
              ) : (
                <>
                  <Box component="i" className="fa-regular fa-plus" />
                  <Typography sx={styles.moreRowLabel}>
                    More recommendations
                  </Typography>
                </>
              )}
            </Box>
          )}
        {!isPolling[tab as TabPage] &&
          !fetchLoading &&
          drafts?.length === 0 && (
            <Grid sx={{ px: 2 }}>
              {incompleteOnboarding && isMobileScreen ? (
                <>
                  Before we can create recommendations, you need to{" "}
                  <MuiLink
                    onClick={() => {
                      trackEvent(
                        "Recommendations Page Complete Onboarding Clicked",
                      );
                      if (incompleteProfile) {
                        setProfileDialogOpen(true);
                      } else {
                        setCreateTemplateOnboardingDialogOpen(true);
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <strong>
                      fill out your profile and create your first template
                    </strong>
                  </MuiLink>
                </>
              ) : (
                `No brands with autogenerated drafts on this list.`
              )}
            </Grid>
          )}
      </Grid>
      {tab === TabPage.bento_recommendations &&
        !incompleteOnboarding &&
        !(isPolling[tab as TabPage] && drafts?.length === 0) && (
          <Grid item sx={styles.explanation}>
            {isPolling[tab as TabPage] ? (
              <>
                <LinearProgress sx={{ my: 1 }} />
                <Typography sx={{ fontSize: 12 }}>
                  Fetching all your new recommendations...
                </Typography>
              </>
            ) : (
              <>
                <Button
                  sx={styles.refreshButton}
                  disableElevation
                  size="small"
                  onClick={handleRefreshBrands}
                >
                  Refresh Recommendations
                  <Box
                    component="i"
                    className="fa-regular fa-refresh"
                    sx={{ ml: 1 }}
                  />
                </Button>
                <Typography sx={{ fontSize: 12 }}>
                  Adjust your <Link to={`/${routes.profile}`}>profile</Link> for
                  better fitting recommendations
                </Typography>
              </>
            )}
          </Grid>
        )}
    </>
  );
};

export default DraftsBrandList;
